export class IndexedDBStore {
  databaseName: string;
  db: IDBDatabase | null;
  tableName: string;
  actualVersion: number;
  constructor(databaseName: string) {
    this.databaseName = databaseName;
    this.db = null;
    this.tableName = "default";
    this.actualVersion = 4;
  }

  openDatabase(tableName: string): Promise<IndexedDBStore> {
    this.tableName = tableName;
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(
        this.databaseName,
        this.actualVersion
      );

      request.onerror = event => {
        reject({ event, error: "Error opening database" });
      };

      request.onsuccess = (event: any) => {
        this.db = event.target.result;
        resolve(this);
      };

      request.onupgradeneeded = (event: any) => {
        const oldVersion = event.oldVersion;
        this.db = event.target.result;
        if (oldVersion > 0 && oldVersion < this.actualVersion) {
          if (this.db?.objectStoreNames?.contains("filterFix")) {
            this.db?.deleteObjectStore("filterFix");
          }
          if (this.db?.objectStoreNames?.contains("sortFix")) {
            this.db?.deleteObjectStore("sortFix");
          }
        }
        this.db?.createObjectStore("filterFix", {
          keyPath: "name"
        });
        this.db?.createObjectStore("sortFix", {
          keyPath: "name"
        });
      };
    });
  }

  closeDatabase() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  update(name: string, newData: Record<string, any>) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject({ error: "Database not open" });
      } else {
        const transaction = this.db.transaction([this.tableName], "readwrite");
        const objectStore = transaction.objectStore(this.tableName);
        const getRequest = objectStore.get(name);

        getRequest.onsuccess = (event: any) => {
          const existingData = event.target.result;
          if (existingData) {
            const putRequest = objectStore.put(newData);

            putRequest.onsuccess = () => {
              resolve("Item updated successfully");
            };

            putRequest.onerror = () => {
              reject({ error: "Error updating item" });
            };
          } else {
            reject({ error: 404 });
          }
        };

        getRequest.onerror = () => {
          reject({ error: "Error retrieving item" });
        };
      }
    });
  }

  create(data: Record<string, any>) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject({ error: "Database not open" });
      } else {
        const transaction = this.db.transaction([this.tableName], "readwrite");
        const objectStore = transaction.objectStore(this.tableName);
        const request = objectStore.add(data);

        request.onerror = event => {
          reject({ event, error: "Error adding data" });
        };

        request.onsuccess = event => {
          resolve({ event, msg: "Data added successfully" });
        };
      }
    });
  }

  async createOrUpdate(data: Record<string, any>) {
    if (!this.db) {
      return { error: "Database not open" };
    }
    const existData = (await this.get(data.name)) as Record<
      string,
      string
    > | null;
    if (existData?.name) return await this.update(existData.name, data);
    return await this.create(data);
  }

  deleteItem(name: string) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject({ error: "Database not open" });
      } else {
        const transaction = this.db.transaction([this.tableName], "readwrite");
        const objectStore = transaction.objectStore(this.tableName);

        const deleteRequest = objectStore.delete(name);

        deleteRequest.onsuccess = () => {
          resolve("Item deleted successfully");
        };

        deleteRequest.onerror = () => {
          reject({ error: "Error deleting item" });
        };
      }
    });
  }

  get(name: string): Promise<Record<string, any> | null> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject({ error: "Database not open" });
      } else {
        const transaction = this.db.transaction([this.tableName], "readonly");
        const objectStore = transaction.objectStore(this.tableName);
        const request = objectStore.get(name);

        request.onerror = event => {
          reject({ event, msg: "Error retrieving data" });
        };

        request.onsuccess = (event: any) => {
          const data = event.target.result;
          if (data) {
            resolve(data);
          } else {
            resolve(null);
          }
        };
      }
    });
  }

  clearTable() {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject({ error: "Database not open" });
      } else {
        if (!this.db.objectStoreNames.contains(this.tableName))
          return reject({
            event: 404,
            error: "Error table not found: " + this.tableName
          });
        const transaction = this.db.transaction([this.tableName], "readwrite");
        const objectStore = transaction.objectStore(this.tableName);
        const clearRequest = objectStore.clear();

        clearRequest.onsuccess = event => {
          resolve({ event, success: "Table cleared successfully" });
        };

        clearRequest.onerror = event => {
          reject({ event, error: "Error clearing table" });
        };
      }
    });
  }

  dropDatabase() {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject({ error: "Database not open" });
      } else {
        this.db.close();
        const deleteRequest = window.indexedDB.deleteDatabase(
          this.databaseName
        );

        deleteRequest.onsuccess = event => {
          resolve({ event, success: "Database drop successfully" });
        };

        deleteRequest.onerror = event => {
          reject({ event, error: "Error drop database" });
        };
      }
    });
  }
}

interface Filters {
  indexDB: IndexedDBStore;
  name: string;
  filters: Record<string, any>;
}

interface Sort {
  name: string;
  sort: Record<string, any>;
}

export function setPersistFilters({ filters, name }: Filters) {
  const instanceDB = new IndexedDBStore("persistentGeneric");
  instanceDB.openDatabase("filterFix").then(db => {
    db.createOrUpdate({ name, filters });
  });
}

export async function getPersistFilters({ filters = [], name }: Filters) {
  const instanceDB = new IndexedDBStore("persistentGeneric");
  let newFilters = filters;
  await instanceDB.openDatabase("filterFix").then(async db => {
    await db.get(name).then(result => result && (newFilters = result.filters));
  });
  return newFilters;
}

export async function removePersistFilters(name: string) {
  const instanceDB = new IndexedDBStore("persistentGeneric");
  await instanceDB.openDatabase("filterFix").then(async db => {
    await db.deleteItem(name);
  });
}

export function setPersistSort({ sort, name }: Sort) {
  const instanceDB = new IndexedDBStore("persistentGeneric");
  instanceDB.openDatabase("sortFix").then(db => {
    db.createOrUpdate({ name, sort });
  });
}

export async function getPersistSort({ sort = {}, name }: Sort) {
  const instanceDB = new IndexedDBStore("persistentGeneric");
  let newsort = sort;
  await instanceDB.openDatabase("sortFix").then(async db => {
    await db.get(name).then(result => result && (newsort = result.sort));
  });
  return newsort;
}

export async function dropDatabase() {
  const instanceDB = new IndexedDBStore("persistentGeneric");
  await instanceDB.openDatabase("filterFix");
  const result = await instanceDB.dropDatabase();
  return result;
}
